import { useLocation, useNavigate } from "infrastructure/router";
import { compareIds } from "util/support";
import type { WithStep } from "./types";

export const useOnboardingStep = ({
    route,
    getApplicationModel
}: WithStep) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const getCurrentStepIndex = () => Number(
        pathname.split('/').pop()
    );

    const getCompletionStats = () => {
        const stats = [];
        let step = 0;

        for (const {
            isIncomplete,
            hasRequestedChange,
            countable = true
        } of Array.from(getApplicationModel().values())) {
            if (!countable) {
                continue;
            }

            step += 1;

            if (Object.is(step, getCurrentStepIndex())) {
                stats.push({
                    step,
                    isCompleted: true
                });
                continue;
            }

            if (isIncomplete || hasRequestedChange) {
                stats.push({
                    step,
                    isCompleted: false
                });
                continue;
            }

            stats.push({
                step,
                isCompleted: true
            });
        }

        return stats;
    };

    const navigateForward = () => {
        const currentStepIndex = getCurrentStepIndex();
        const basePath = pathname.replace(/\/\d+$/, '');
        const stats = getCompletionStats();

        const before = stats.slice(0, currentStepIndex - 1);
        const after = stats.slice(currentStepIndex);

        for (const { isCompleted, step: nextStepIndex } of [
            ...after,
            ...before
        ]) {
            if (!isCompleted) {
                return navigate(`${basePath}/${nextStepIndex}`);
            }
        }

        navigate(route.Complete);
    };

    const isCurrentStep = (step: string | number) => (
        compareIds(getCurrentStepIndex() ?? '', step)
    );

    const getCompletedStepsCount = () => {
        let completedStepsCount = 0;

        getApplicationModel()
            .forEach(({ countable = true, isIncomplete }) => {
                if (countable && !isIncomplete) {
                    completedStepsCount++;
                }
            });

        return completedStepsCount;
    };


    const getTotalStepsCount = () => {
        let totalStepsCount = 0;

        getApplicationModel()
            .forEach(({ countable = true }) => {
                if (countable) {
                    totalStepsCount++;
                }
            });

        return totalStepsCount;
    };

    const getStepModel = (step: number) => (
        getApplicationModel().get(step)
    );

    const getCurrentStepModel = () => getStepModel(getCurrentStepIndex());

    return {
        getCompletedStepsCount,
        getTotalStepsCount,
        getCurrentStepIndex,
        getCurrentStepModel,
        getStepModel,
        isCurrentStep,
        navigateForward,
        navigate
    };
};

export default useOnboardingStep;
